import React, { useCallback } from 'react';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import MainNavigator from './src/navigation/MainNavigator';
import { AuthProvider } from './src/contexts/AuthContext';
import { SidebarProvider } from './src/contexts/SidebarContext';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { LanguageProvider } from './src/contexts/LanguageContext';
import { YearProvider } from './src/contexts/YearContext';
import { navigationRef } from './src/services/navigationService';
import './src/i18n';
import { COLORS } from './src/constants/config';
import linking from './src/navigation/linking';
import TalentRegistrationScreen from './src/screens/talents/TalentRegistrationScreen';

// Inject global scrollbar styles on web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    * { scrollbar-width: unset !important; scrollbar-color: unset !important; }
    *::-webkit-scrollbar { width: 8px !important; height: 8px !important; display: block !important; }
    *::-webkit-scrollbar-track { background: #f0f0f0 !important; border-radius: 4px !important; }
    *::-webkit-scrollbar-thumb { background-color: #bbb !important; border-radius: 4px !important; }
    *::-webkit-scrollbar-thumb:hover { background-color: #999 !important; }
  `;
  document.head.appendChild(style);
}

SplashScreen.preventAutoHideAsync();

export default function App() {
  const [fontsLoaded] = useFonts({
    'JF-Flat-Regular': require('./assets/fonts/JF-Flat-Regular.ttf'),
    'JF-Flat-Medium': require('./assets/fonts/JF-Flat-Medium.ttf'),
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
      </View>
    );
  }

  // Public registration page — outside AuthProvider (no login required)
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const path = window.location.pathname;
    // Public registration: clean top-level /register (per-dept link, e.g.
    // /register?dept=75) plus the legacy /talents/register path.
    // Exact match on the legacy path, not startsWith: '/talents/register-talent'
    // is the authenticated in-app screen and must not be shadowed by the
    // public form on a direct load or refresh.
    if (path === '/register' || path === '/talents/register') {
      return (
        <GestureHandlerRootView style={styles.container} onLayout={onLayoutRootView}>
          <SafeAreaProvider>
            <ThemeProvider>
              <LanguageProvider>
                <TalentRegistrationScreen />
              </LanguageProvider>
            </ThemeProvider>
          </SafeAreaProvider>
        </GestureHandlerRootView>
      );
    }
  }

  return (
    <GestureHandlerRootView style={styles.container} onLayout={onLayoutRootView}>
      <SafeAreaProvider>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <SidebarProvider>
                <YearProvider>
                  <ThemedApp />
                </YearProvider>
              </SidebarProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

function ThemedApp() {
  const { isDark } = useTheme();

  return (
    <>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <NavigationContainer
        ref={navigationRef}
        linking={linking}
        documentTitle={{
          formatter: () => 'نظام مبادرة مواهب',
        }}
      >
        <MainNavigator />
      </NavigationContainer>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
